import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { companyService, userService } from '../../services/api';

const roleHierarchy = { companyAdmin: 3, manager: 2, employee: 1 };

const AddUserModal = ({ open, onClose, onSubmit }) => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [companies, setCompanies] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    companyId: currentUser.company || '',
    reportsTo: '',
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await companyService.getCompanies();
        setCompanies(response.data);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }
    };
    if (open) fetchCompanies();
  }, [open]);

  // Fetch users for "Reporting to" whenever company or role changes
  useEffect(() => {
    const companyId = formData.companyId;
    const role = formData.role;
    if (!companyId || !role) { setCompanyUsers([]); return; }
    const currentLevel = roleHierarchy[role] || 0;
    if (currentLevel === 0) { setCompanyUsers([]); return; }

    const fetchUsers = async () => {
      try {
        const response = await userService.getUsers(companyId);
        const higher = response.data.filter(u =>
          (roleHierarchy[u.role] || 0) > currentLevel
        );
        setCompanyUsers(higher);
      } catch (e) {
        console.error('Failed to fetch company users:', e);
      }
    };
    fetchUsers();
  }, [formData.companyId, formData.role, currentUser.role]);

  useEffect(() => {
    if (!open) {
      setFormData({ name: '', email: '', role: '', companyId: currentUser.company || '', reportsTo: '' });
      setCompanyUsers([]);
    }
  }, [open, currentUser.company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
      // reset reportsTo when role or company changes
      ...(name === 'role' || name === 'companyId' ? { reportsTo: '' } : {}),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.role) return;
    if (['companyAdmin', 'manager', 'employee'].includes(formData.role) && !formData.companyId) return;
    onSubmit(formData);
  };

  const getRoleOptions = () => {
    if (currentUser.role === 'superAdmin') {
      return ['coach', 'companyAdmin'];
    } else if (currentUser.role === 'coach') {
      return ['companyAdmin', 'manager' , 'employee'];
    } else if (currentUser.role === 'companyAdmin') {
      return ['manager', 'employee'];
    } else if (currentUser.role === 'manager') {
      return ['employee'];
    }
    return [];
  };

  const isFormValid = () => {
    const requiredFields = ['name', 'email', 'role'];
    const hasRequiredFields = requiredFields.every(field => !!formData[field]);
    const needsCompany = ['companyAdmin', 'manager', 'employee'].includes(formData.role);

    return hasRequiredFields && (!needsCompany || !!formData.companyId);
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                  required
                  label="Name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
              />
              <TextField
                  required
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
              />
              <FormControl required fullWidth>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                    labelId="role-label"
                    name="role"
                    value={formData.role || ''}
                    onChange={handleChange}
                    label="Role"
                >
                  {getRoleOptions().map((role) => (
                      <MenuItem key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(formData.role === 'companyAdmin' || formData.role === 'manager' || formData.role === 'employee') && (
                  <FormControl required fullWidth>
                    <InputLabel id="company-label">Company</InputLabel>
                    <Select
                        labelId="company-label"
                        name="companyId"
                        value={formData.companyId || ''}
                        onChange={handleChange}
                        label="Company"
                    >
                      {companies.map((company) => (
                          <MenuItem key={company._id} value={company._id}>
                            {company.name}
                          </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
              )}
              {companyUsers.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel id="reports-to-label">Reporting to</InputLabel>
                  <Select
                    labelId="reports-to-label"
                    name="reportsTo"
                    value={formData.reportsTo || ''}
                    onChange={handleChange}
                    label="Reporting to"
                  >
                    <MenuItem value="">— None —</MenuItem>
                    {companyUsers.map((u) => (
                      <MenuItem key={u._id} value={u._id}>
                        {u.name} ({u.role === 'companyAdmin' ? 'Company Admin' : u.role.charAt(0).toUpperCase() + u.role.slice(1)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isFormValid()}
            >
              Add User
            </Button>
          </DialogActions>
        </form>
      </Dialog>
  );
};

export default AddUserModal;